import React, { useMemo, useState } from 'react';
import { X, Upload, CheckCircle2, AlertCircle, ArrowRight, Loader2 } from 'lucide-react';
import { usersApi } from '../api';

const VALID_ROLES = ['Admin', 'AllIndiaManager', 'ZoneManager', 'RetailManager', 'EBO', 'Warehouse', 'Logistics'];
const VALID_ZONES = ['North', 'South', 'East', 'West'];
const DOMAIN_RE = /@(futuremakeup\.in|futuremakeup\.com)$/i;

/** Split into rows on \n, cells on \t OR comma (auto-detect per line). */
function parseClipboardText(text) {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  return lines.map(line => (line.includes('\t') ? line.split('\t') : line.split(','))
    .map(c => c.trim()));
}

const HEADERS = ['name', 'email', 'role', 'scope', 'branchcode', 'zone'];
const isHeaderRow = (cells) => {
  const lower = cells.map(c => c.toLowerCase());
  return lower.includes('email') && (lower.includes('name') || lower.includes('role'));
};

function rowsFromText(text) {
  const raw = parseClipboardText(text);
  if (raw.length === 0) return { headers: null, rows: [] };

  let headers = null;
  let dataStart = 0;
  if (isHeaderRow(raw[0])) {
    headers = raw[0].map(h => h.toLowerCase().replace(/\s+/g, ''));
    dataStart = 1;
  }
  const defaultOrder = ['name', 'email', 'role', 'scope', 'branchcode', 'zone'];
  const keyOrder = headers || defaultOrder;

  return {
    headers,
    rows: raw.slice(dataStart).map(cells => {
      const row = {};
      keyOrder.forEach((k, i) => { if (cells[i] !== undefined) row[k] = cells[i]; });
      return {
        name: row.name || '',
        email: (row.email || '').toLowerCase(),
        role: row.role || '',
        scope: row.scope || 'All branches',
        branchCode: row.branchcode || row.branchCode || '',
        zone: row.zone || '',
      };
    }),
  };
}

function validate(row) {
  if (!row.name) return { ok: false, error: 'name required' };
  if (!row.email || !DOMAIN_RE.test(row.email)) return { ok: false, error: 'email must be @futuremakeup.in or @futuremakeup.com' };
  if (!VALID_ROLES.includes(row.role)) return { ok: false, error: `role must be one of ${VALID_ROLES.join(', ')}` };
  if (row.role === 'EBO' && !row.branchCode) return { ok: false, error: 'EBO role needs branchCode' };
  if (row.role === 'ZoneManager' && !VALID_ZONES.includes(row.zone)) return { ok: false, error: `ZoneManager needs zone (${VALID_ZONES.join('|')})` };
  return { ok: true };
}

const EXAMPLE = `name\temail\trole\tscope\tbranchCode\tzone
Priya Sharma\tpriya@futuremakeup.in\tAdmin\tAll branches\t\t
Ravi Kumar\tnorth.manager@futuremakeup.in\tZoneManager\tNorth zone\t\tNorth
Anita Rao\tebo.blr01@futuremakeup.in\tEBO\tBLR-01\tEBO-BLR-01\t`;

export default function BulkImportUsersModal({ open, onClose, onDone, theme }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {inserted, skipped, failed, invitesSent, rows}

  const parsed = useMemo(() => rowsFromText(text), [text]);
  const validated = useMemo(() => parsed.rows.map(r => ({ ...r, ...validate(r) })), [parsed]);
  const validRows = validated.filter(r => r.ok);
  const invalidCount = validated.length - validRows.length;

  if (!open) return null;

  const submit = async () => {
    if (validRows.length === 0) return;
    setLoading(true);
    setResult(null);
    try {
      const cleanRows = validRows.map(({ ok, error, ...rest }) => rest); // eslint-disable-line no-unused-vars
      const r = await usersApi.bulk(cleanRows);
      setResult(r);
      if (r.inserted > 0) onDone?.(r);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || 'Bulk import failed' });
    } finally {
      setLoading(false);
    }
  };

  const closeAndReset = () => { setText(''); setResult(null); onClose(); };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={closeAndReset}>
      <div className="fm-card p-5 w-full max-w-[900px] max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="bulk-import-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-semibold text-[16px]">Bulk import users</div>
            <div className="text-[12px] text-[#7a6f5c] mt-0.5">
              Paste rows from Excel (tab-separated) or comma-separated. First row can optionally be the header line.
            </div>
          </div>
          <button onClick={closeAndReset} data-testid="bulk-import-close-btn"><X size={16} /></button>
        </div>

        {!result && (
          <>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="text-[12.5px] font-medium block mb-1">Paste rows</label>
                <textarea
                  data-testid="bulk-import-textarea"
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder={EXAMPLE}
                  spellCheck={false}
                  className="fm-focus w-full h-[240px] font-mono text-[12px] border border-[#E7E2D3] rounded-md p-3 bg-white leading-relaxed"
                />
              </div>
              <div className="text-[12.5px] text-[#7a6f5c]">
                <div className="font-semibold text-[#232323] mb-1">Column order</div>
                <ol className="list-decimal ml-4 space-y-0.5">
                  <li><span className="font-mono">name</span> — required</li>
                  <li><span className="font-mono">email</span> — required, must end with @futuremakeup.in or @futuremakeup.com</li>
                  <li><span className="font-mono">role</span> — one of {VALID_ROLES.join(', ')}</li>
                  <li><span className="font-mono">scope</span> — optional, defaults to "All branches"</li>
                  <li><span className="font-mono">branchCode</span> — required if role = EBO, else blank</li>
                  <li><span className="font-mono">zone</span> — required if role = ZoneManager (North / South / East / West)</li>
                </ol>
                <div className="mt-3 pt-3 border-t border-[#EFEADC]">
                  <div className="font-semibold text-[#232323] mb-1">Tip</div>
                  Copy directly from Excel/Google Sheets — cells are tab-separated so parsing "just works". Duplicate emails are skipped (already exist). Rows with bad emails/roles are flagged before import so you can fix them.
                </div>
              </div>
            </div>

            {parsed.rows.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-3 mb-2 text-[12.5px]">
                  <span className="text-[#7a6f5c]">Parsed:</span>
                  <span className="font-semibold" data-testid="bulk-import-total">{parsed.rows.length} rows</span>
                  <span className="inline-flex items-center gap-1 text-[#2f6b41]"><CheckCircle2 size={12} /> <span data-testid="bulk-import-valid-count">{validRows.length} ready</span></span>
                  {invalidCount > 0 && <span className="inline-flex items-center gap-1" style={{ color: '#B84A4A' }}><AlertCircle size={12} /> {invalidCount} need fixing</span>}
                </div>
                <div className="border border-[#E7E2D3] rounded-md max-h-[240px] overflow-auto">
                  <table className="w-full text-[12.5px]">
                    <thead className="bg-[#F7F4EA] sticky top-0">
                      <tr className="text-left text-[11px] text-[#7a6f5c]">
                        <th className="px-2 py-1.5">#</th>
                        <th className="px-2 py-1.5">Name</th>
                        <th className="px-2 py-1.5">Email</th>
                        <th className="px-2 py-1.5">Role</th>
                        <th className="px-2 py-1.5">Zone / Branch</th>
                        <th className="px-2 py-1.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validated.map((r, i) => (
                        <tr key={i} className="border-t border-[#EFEADC]" data-testid={`bulk-import-row-${i}`}>
                          <td className="px-2 py-1.5 text-[#7a6f5c]">{i + 1}</td>
                          <td className="px-2 py-1.5">{r.name || <span className="text-[#B84A4A]">—</span>}</td>
                          <td className="px-2 py-1.5 font-mono">{r.email || <span className="text-[#B84A4A]">—</span>}</td>
                          <td className="px-2 py-1.5">{r.role || <span className="text-[#B84A4A]">—</span>}</td>
                          <td className="px-2 py-1.5 font-mono text-[11.5px]">{r.role === 'EBO' ? r.branchCode : r.role === 'ZoneManager' ? r.zone : ''}</td>
                          <td className="px-2 py-1.5">
                            {r.ok ? <span className="inline-flex items-center gap-1 text-[#2f6b41]"><CheckCircle2 size={12} /> ready</span>
                              : <span className="inline-flex items-center gap-1" style={{ color: '#B84A4A' }} title={r.error}><AlertCircle size={12} /> {r.error}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={closeAndReset} className="px-3 py-2 border border-[#E7E2D3] rounded-md text-[13.5px]">Cancel</button>
              <button
                onClick={submit}
                disabled={loading || validRows.length === 0}
                data-testid="bulk-import-submit-btn"
                className="px-4 py-2 rounded-md text-white text-[13.5px] font-medium disabled:opacity-40 inline-flex items-center gap-2"
                style={{ background: theme.primary }}
              >
                {loading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                Import {validRows.length} user{validRows.length === 1 ? '' : 's'}
              </button>
            </div>
          </>
        )}

        {result && (
          <div data-testid="bulk-import-result">
            {result.error ? (
              <div className="p-4 rounded bg-[#F2E2E2] text-[#a04747] text-[13px]">{result.error}</div>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <ResultTile label="Created" value={result.inserted} color="#2f6b41" />
                  <ResultTile label="Skipped" value={result.skipped} color="#7a6f5c" />
                  <ResultTile label="Invites sent" value={result.invitesSent} color="#2f6b41" />
                  <ResultTile label="Failed" value={result.failed} color="#B84A4A" />
                </div>
                {(result.rows || []).some(r => r.status !== 'inserted' || r.inviteSent === false) && (
                  <div className="border border-[#E7E2D3] rounded-md max-h-[220px] overflow-auto mb-4">
                    <table className="w-full text-[12px]">
                      <thead className="bg-[#F7F4EA] sticky top-0">
                        <tr className="text-left text-[11px] text-[#7a6f5c]">
                          <th className="px-2 py-1.5">#</th>
                          <th className="px-2 py-1.5">Email</th>
                          <th className="px-2 py-1.5">Result</th>
                          <th className="px-2 py-1.5">Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.rows.map((r, i) => (
                          <tr key={i} className="border-t border-[#EFEADC]">
                            <td className="px-2 py-1.5 text-[#7a6f5c]">{(r.row ?? i) + 1}</td>
                            <td className="px-2 py-1.5 font-mono">{r.email}</td>
                            <td className="px-2 py-1.5">
                              {r.status === 'inserted' && <span className="text-[#2f6b41]">✓ created</span>}
                              {r.status === 'skipped' && <span className="text-[#7a6f5c]">— skipped</span>}
                              {r.status === 'failed' && <span style={{ color: '#B84A4A' }}>✗ failed</span>}
                            </td>
                            <td className="px-2 py-1.5 text-[#7a6f5c]">{r.reason || (r.status === 'inserted' && r.inviteSent === false ? 'invite email failed' : '')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button onClick={closeAndReset} data-testid="bulk-import-done-btn" className="px-4 py-2 rounded-md text-white text-[13.5px] font-medium inline-flex items-center gap-2" style={{ background: theme.primary }}>
                    Done <ArrowRight size={13} />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ResultTile({ label, value, color }) {
  return (
    <div className="border border-[#E7E2D3] rounded-md px-3 py-2 bg-white">
      <div className="text-[11px] text-[#7a6f5c]">{label}</div>
      <div className="text-[22px] font-semibold" style={{ color }}>{value}</div>
    </div>
  );
}
